import { filter, take } from 'rxjs/operators';
import { BillingError } from 'src/app/pages/new-billing/models/billing-error';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import {
  DialogOptions,
  UpgradeDowngradeBaseModel
} from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-base.model';
import {
  UpgradeBillingData,
  UpgradeBillingDetailsComponent,
  UpgradeBillingResult
} from 'src/app/pages/new-billing/upgrade-billing-details/upgrade-billing-details.component';
import { PaymentSummaryOptions } from 'src/app/pages/new-subscribe-plans/store/model';

export class UpgradeDowngradeOnPaidModel extends UpgradeDowngradeBaseModel {
  public handleError(err: unknown, cardPlan: ConcretePlan): void {
    if (err instanceof BillingError && err.errorCode === 'subscription_payment_declined') {
      this.handlePaymentDeclinedError(cardPlan);
      return;
    }

    this.dialogRef.componentInstance.errorMsg = 'billing.switchPlan.errorSwitching';
  }

  public openDialog(
    cardPlan: ConcretePlan,
    { actionType, paymentRecurrence, trackingContext, previousPlanId, hasFixedRecurrence }: DialogOptions,
    _isV2 = false,
  ) {
    this.captureDialogTrackingContext(trackingContext);
    this.capturePreviousPlanIdForTracking(previousPlanId);
    if (paymentRecurrence) cardPlan.switchPlanRecurrence(paymentRecurrence);
    const btnText = this.translateService.instant(`common.${actionType}`).toUpperCase();
    const showDiscountRemovalNote = this.growthBookService.getFeatureValue('billing:discount_removal_companies', false);

    this.dialogRef = this.matDialog.open<UpgradeBillingDetailsComponent, UpgradeBillingData, UpgradeBillingResult>(
      UpgradeBillingDetailsComponent,
      {
        data: {
          btnText,
          currentPlan: cardPlan,
          hasFixedRecurrence,
          paymentRecurrence: paymentRecurrence ?? cardPlan.paymentModel.paymentRecurrence,
          onSwitchPlan: (dialogCardPlan: ConcretePlan) => this.handleSwitchPlan(dialogCardPlan, actionType),
          actionType,
          ...(this.previousPlanIdForTracking ? { previousPlanId: this.previousPlanIdForTracking } : {}),
          ...(trackingContext ? { trackingContext } : {}),
          ...(showDiscountRemovalNote
            ? { footerNoteHtml: this.translateService.instant('billing.discountRemovalNote') }
            : {}),
        },
        autoFocus: false,
        disableClose: true,
      },
    );

    return this.dialogRef;
  }

  private handlePaymentDeclinedError(cardPlan: ConcretePlan) {
    const checkoutDialogRef = this.billingActions.openCheckoutDialog({
      currentPlan: cardPlan,
      isUpdatingCard: true,
      paymentFailureSummaryData: {
        cardInfo: null,
        planInfo: {
          name: cardPlan.name,
          unitPrice: cardPlan.paymentModel.nextPaymentPerUser,
          recurrence: cardPlan.paymentModel.paymentRecurrence,
        },
        hasAddons: cardPlan.paymentModel.hasAddons,
        nextPaymentDate: null,
        billableUsers: cardPlan.paymentModel.billableUsers,
        totalAmount: cardPlan.paymentModel.nextPaymentAmountCalculated,
        ...this.getPaymentFailureSummaryOptions(),
      },
      withOverlayLoader: false,
      trackingCtx: {},
    });

    checkoutDialogRef.afterClosed().pipe(
      take(1),
      filter(result => !!result?.billingDetails),
    ).subscribe(({ billingDetails }) => {
      const dialog = this.dialogRef.componentInstance as UpgradeBillingDetailsComponent;
      if (dialog?.cardPlan?.paymentModel) {
        dialog.cardPlan.paymentModel.cardLast4 = billingDetails.cardLastFourDigits;
      }
    });
  }

  private getPaymentFailureSummaryOptions(): PaymentSummaryOptions {
    return {
      title: 'billing.paymentSummary.failure.confirmTitle',
      description: 'billing.paymentSummary.failure.confirmMessage',
      type: 'warning',
      paymentSummaryText: 'billing.paymentSummary.failure.paymentSummaryTxt',
      footerText: this.translateService.instant('billing.paymentSummary.failure.footerText', {
        termsUrl: 'https://www.timedoctor.com/terms-of-service',
      }),
    };
  }
}
